import React from 'react'
import {
  TextField,
  MessageBar,
  MessageBarType,
  Separator,
  ITag,
  INavLink,
  Stack,
} from 'office-ui-fabric-react'
import { ChildProps, graphql } from 'react-apollo'
import {
  createOutstreamPlacement,
  updateOutstreamPlacement,
  OutstreamPlacementInfo,
  OutstreamPlacementCollectionTagsResponse,
  getOutstreamPlacementCollectionTagsQuery,
  OutstreamPlacementIntegrationType,
} from '../../Api/outstreamPlacements_v2'
import { ErrorDialog } from '../../Components/ErrorDialog'
import { TagInput } from '../../Components/Input/TagInput'
import { cloneDeep, uniq } from 'lodash'
import { IntegrationLimitingForm } from '../../Components/Input/IntegrationLimitingForm'
import {
  devicesToTargetingRules,
  targetingRulesToDevices,
} from '../../Components/Input/DeviceTargetingForm'
import { isDefined } from '../../Utils/Fp'
import { IntegrationDropdown } from '../../Components/Input/IntegrationDropdown'
import LargeDialogWithSidebar from '../../Components/LargeDialogWithSidebar'
import { VideoCollectionDropdown } from '../../Components/Input/VideoCollectionDropdown'
import { NumberInput } from '../../Components/Input/NumberInput'
import InfoBox from '../CampaignCollectionScreen/CampaignDialog/InfoBox'
import {
  getErrorCode as getIntegrationTypeErrorCode,
  OutstreamPlacementIntegrationForm,
} from './OutstreamPlacementIntegrationForm'
import { isAccountManager, isAdmin } from '../../Api/session'

type FormErrorSet = { [fieldName: string]: string }

interface State {
  modifiedPlacement: OutstreamPlacementInfo
  isLoading: boolean
  hasError: boolean
  childComponentErrors: {
    limiting: boolean
  }
  form: {
    errors: FormErrorSet
  }
}

interface InputProps {
  modifiedPlacement?: OutstreamPlacementInfo
  collectionId: string
  onClose: () => void
  onUpdated: () => void
}

const now = new Date().toISOString()

const defaultOutstreamPlacement: OutstreamPlacementInfo = {
  id: '',
  creationDate: now,
  title: '',
  integration: '',
  videoContentCollectionId: undefined,
  tags: [],
  whitelistGlobs: [],
  blacklistGlobs: [],
  targetingRules: [],
  maximumAdLength: undefined,
  bannerDurationSeconds: undefined,
  integrationType: OutstreamPlacementIntegrationType.CUSTOM,
  integrationTypeConfig: {},
}

export class CreateOutstreamPlacementDialogView extends React.Component<
  ChildProps<InputProps, OutstreamPlacementCollectionTagsResponse>,
  State
> {
  constructor(props: any) {
    super(props)
    const state = {
      modifiedPlacement: cloneDeep(
        this.props.modifiedPlacement || defaultOutstreamPlacement
      ),
      hasError: false,
      isLoading: false,
      form: {
        errors: {},
      },
      childComponentErrors: {
        limiting: false,
      },
    } as State
    this.state = state
    if (!this.props.modifiedPlacement) {
      return
    }
    this.state = state
  }
  render() {
    if (this.props.data!.error) {
      return (
        <ErrorDialog
          onClose={this.props.onClose}
          title="Network error"
          message="Could not fetch collection parameters. "
        />
      )
    }
    if (!this.props.data!.outstreamPlacementCollections_v2) {
      return <React.Fragment />
    }
    const gap = 28
    const titleDescription = this.state.modifiedPlacement.title
    const isEdited =
      JSON.stringify(this.props.modifiedPlacement) !==
      JSON.stringify(this.state.modifiedPlacement)
    const dialogTitle =
      (this.props.modifiedPlacement
        ? 'Update Outstream Placement'
        : 'Create Outstream Placement') +
      (titleDescription ? ` "${titleDescription}"` : '') +
      (isEdited ? '*' : '')
    return (
      <LargeDialogWithSidebar
        title={dialogTitle}
        isBlocking={true}
        buttons={[
          {
            name: 'Cancel',
            action: () => this.close(),
            disabled: this.state.isLoading,
          },
          {
            name: this.props.modifiedPlacement ? 'Save' : 'Create',
            action: () => {
              this.submit()
            },
            disabled: this.state.isLoading,
            primary: true,
          },
        ]}
        onClose={() => this.close()}
        sidebarWidth={170}
        categories={
          [
            {
              name: 'General',
              key: 'general',
              icon: 'FileASPX',
            },
            {
              name: 'Integration',
              key: 'integration',
              icon: 'Globe',
            },
            {
              name: 'Content',
              key: 'content',
              icon: 'video',
            },
            {
              name: 'Targeting',
              key: 'targeting',
              icon: 'Bullseye',
            },
          ].filter(Boolean) as INavLink[]
        }
      >
        {[
          {
            title: 'General',
            content: this.getGeneralForm(gap),
          },
          {
            title: 'Integration',
            content: this.getIntegrationForm(gap),
          },
          {
            title: 'Content',
            content: this.getContentForm(gap),
          },
          {
            title: 'Targeting',
            content: this.getTargetingForm(gap),
          },
        ]
          .filter(Boolean)
          .map((item: any, index, a) => (
            <div key={index}>
              {index !== 0 && (
                <Separator styles={{ root: { margin: 32 } }}></Separator>
              )}
              <div>
                <h2
                  id={item.title.toLowerCase()}
                  style={{ marginTop: 0, marginBottom: 42 }}
                >
                  {item.title}
                </h2>
                {item.content}
              </div>
            </div>
          ))}
        {this.state.hasError && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={true}
            styles={{ root: { marginTop: 15 } }}
          >
            Could not create outstream placement. If the problem persist, please
            contact support.
          </MessageBar>
        )}
      </LargeDialogWithSidebar>
    )
  }
  getGeneralForm(gap: number) {
    return (
      <Stack gap={gap}>
        <TextField
          label={'Name'}
          errorMessage={this.state.form.errors.title}
          name={'title'}
          value={this.state.modifiedPlacement.title}
          autoComplete={'off'}
          onChange={(evt: any, value?: string) => {
            this.clearErrors()
            this.setState({
              modifiedPlacement: {
                ...this.state.modifiedPlacement,
                title: value!,
              },
            })
          }}
          disabled={this.state.isLoading}
          required
        />
        <IntegrationDropdown
          style={{
            flexGrow: 1,
            maxWidth: `calc(50% - ${gap / 2}px)`,
          }}
          value={this.state.modifiedPlacement.integration || ''}
          onChange={(integration) => {
            this.clearErrors()
            this.setState({
              modifiedPlacement: {
                ...this.state.modifiedPlacement,
                integration,
              },
            })
          }}
          disabled={this.state.isLoading}
          required={true}
          errorMessage={this.state.form.errors.integration}
        />
        <TagInput
          label="Tags"
          onResolveSuggestions={(filter, tagList): ITag[] => {
            const tagCandidates = [
              ...this.props.data!.outstreamPlacementCollections_v2![0]!.tags,
            ]
            let isNew = false
            if (tagCandidates.indexOf(filter) === -1) {
              tagCandidates.push(filter)
              isNew = true
            }
            const filteredTags = tagCandidates.filter(
              (tag) =>
                !tag.startsWith('opz') &&
                !(tagList || []).find((t) => t.key === tag) &&
                tag.indexOf(filter) !== -1
            )
            return filteredTags.filter(isDefined).map((key) => ({
              key,
              name: key === filter && isNew ? `Create new tag "${key}"` : key,
            }))
          }}
          pickerLabels={{
            suggestionsHeader: 'Suggested Tags',
            noResults: 'Tag already exists',
          }}
          onChange={(value?: ITag[]) => {
            this.clearErrors()
            this.setState({
              modifiedPlacement: {
                ...this.state.modifiedPlacement,
                tags: uniq((value || []).map(({ key }) => key)),
              },
            })
          }}
          itemLimit={50}
          selectedItems={uniq(this.state.modifiedPlacement.tags)
            .filter((tag) => !tag.startsWith('opz'))
            .map((key) => ({ key, name: key }))}
          disabled={this.state.isLoading}
        />
      </Stack>
    )
  }
  getIntegrationForm(gap: number) {
    return (
      <OutstreamPlacementIntegrationForm
        canAccessR2B2IntegrationTypes={
          !!this.props.data &&
          !!this.props.data.session &&
          (isAdmin(this.props.data.session) ||
            isAccountManager(this.props.data.session))
        }
        isAdmin={
          !!this.props.data &&
          !!this.props.data.session &&
          isAdmin(this.props.data.session)
        }
        placementId={
          this.props.modifiedPlacement
            ? this.props.modifiedPlacement.id
            : undefined
        }
        gap={gap}
        integration={this.state.modifiedPlacement.integration}
        integrationType={this.state.modifiedPlacement.integrationType}
        disabled={this.state.isLoading}
        errorCode={this.state.form.errors.integrationType || ''}
        integrationTypeConfig={
          this.state.modifiedPlacement.integrationTypeConfig || {}
        }
        documentation={
          this.props.modifiedPlacement
            ? this.props.modifiedPlacement.documentation
            : undefined
        }
        onChange={({ integrationType, config }) => {
          this.clearErrors()
          this.setState({
            modifiedPlacement: {
              ...this.state.modifiedPlacement,
              integrationType,
              integrationTypeConfig: config,
            },
          })
        }}
      />
    )
  }
  getContentForm(gap: number) {
    return (
      <Stack gap={gap}>
        <VideoCollectionDropdown
          label="Mapped video content collection"
          value={
            this.state.modifiedPlacement.videoContentCollectionId || undefined
          }
          onChange={(videoContentCollectionId: string | undefined) => {
            this.setState({
              modifiedPlacement: {
                ...this.state.modifiedPlacement,
                videoContentCollectionId,
              },
            })
          }}
          disabled={this.state.isLoading}
          required={true}
        />
        {this.state.modifiedPlacement.videoContentCollectionId ? (
          <InfoBox type={MessageBarType.info}>
            A random video from this content collection will be shown when
            there’s been at least one Google IMA impression.
          </InfoBox>
        ) : null}
        <NumberInput
          label={'Banner duration in seconds'}
          errorMessage={this.state.form.errors.bannerDurationSeconds}
          name={'bannerDurationSeconds'}
          value={this.state.modifiedPlacement.bannerDurationSeconds || null}
          min={1}
          onChange={(value?: number) => {
            this.clearErrors()
            this.setState({
              modifiedPlacement: {
                ...this.state.modifiedPlacement,
                bannerDurationSeconds: value,
              },
            })
          }}
          disabled={this.state.isLoading}
        />
        <MessageBar messageBarType={MessageBarType.info}>
          {
            'For how many seconds banner should stay in player. Leave empty to use the global default (15 seconds).'
          }
        </MessageBar>
      </Stack>
    )
  }
  getTargetingForm(gap: number) {
    const isR2B2Replacement =
      this.state.modifiedPlacement.integrationType ===
      OutstreamPlacementIntegrationType.R2B2_REPLACEMENT

    // r2b2 replacement keeps its device targeting in integrationTypeConfig.devices
    // (lowercase, no SmartTV). Bridge the shared picker to that field so both
    // integration styles use one form; every other type uses targetingRules as-is.
    const targetingRules = isR2B2Replacement
      ? devicesToTargetingRules(
          (this.state.modifiedPlacement.integrationTypeConfig || {}).devices ||
            []
        )
      : this.state.modifiedPlacement.targetingRules || []
    const onTargetingRulesChange = (rules: string[]) => {
      if (isR2B2Replacement) {
        this.setState({
          modifiedPlacement: {
            ...this.state.modifiedPlacement,
            integrationTypeConfig: {
              ...(this.state.modifiedPlacement.integrationTypeConfig || {}),
              devices: targetingRulesToDevices(rules),
            },
          },
        })
        return
      }
      this.setState({
        modifiedPlacement: {
          ...this.state.modifiedPlacement,
          targetingRules: rules,
        },
      })
    }

    return (
      <IntegrationLimitingForm
        label={''}
        gap={gap}
        showDeviceTargeting={true}
        deviceOptions={
          isR2B2Replacement ? ['Desktop', 'Tablet', 'Mobile'] : undefined
        }
        targetingRules={targetingRules}
        onTargetingRulesChange={onTargetingRulesChange}
        integrationLimits={{
          whitelistGlobs: this.state.modifiedPlacement.whitelistGlobs,
          blacklistGlobs: this.state.modifiedPlacement.blacklistGlobs,
          maximumAdLength: this.state.modifiedPlacement.maximumAdLength,
        }}
        disabled={this.state.isLoading}
        errorMessages={{}}
        onChange={(integrationLimits, hasError) => {
          const {
            whitelistGlobs = this.state.modifiedPlacement.whitelistGlobs,
            blacklistGlobs = this.state.modifiedPlacement.blacklistGlobs,
            maximumAdLength = this.state.modifiedPlacement.maximumAdLength,
          } = integrationLimits
          this.setState({
            modifiedPlacement: {
              ...this.state.modifiedPlacement,
              whitelistGlobs,
              blacklistGlobs,
              maximumAdLength: maximumAdLength || undefined,
            },
            childComponentErrors: {
              limiting: hasError,
            },
          })
        }}
        showMaximumAdLength={true}
      />
    )
  }
  clearErrors() {
    this.setState({
      hasError: false,
      form: {
        errors: {
          title: '',
          integration: '',
        },
      },
    })
  }
  submit() {
    if (Object.values(this.state.childComponentErrors).some(Boolean)) {
      return
    }
    const title = this.state.modifiedPlacement.title
    if (!title || !title.trim()) {
      this.setState({
        form: {
          errors: {
            title: 'field is mandatory',
          },
        },
      })
      return
    }
    const integration = this.state.modifiedPlacement.integration
    if (!integration) {
      this.setState({
        form: {
          errors: {
            integration: 'field is mandatory',
          },
        },
      })
      return
    }
    this.setState({ isLoading: true })
    const isCreation = !this.props.modifiedPlacement
    const tags = this.state.modifiedPlacement.tags
    if (!isCreation) {
      tags.push(this.props.modifiedPlacement!.id)
    }
    const whitelistGlobs = this.state.modifiedPlacement.whitelistGlobs
      ? this.state.modifiedPlacement.whitelistGlobs.filter(Boolean)
      : []
    const blacklistGlobs = this.state.modifiedPlacement.blacklistGlobs
      ? this.state.modifiedPlacement.blacklistGlobs.filter(Boolean)
      : []
    const maximumAdLength =
      this.state.modifiedPlacement.maximumAdLength &&
      this.state.modifiedPlacement.maximumAdLength > 0
        ? this.state.modifiedPlacement.maximumAdLength
        : null
    const bannerDurationSeconds =
      this.state.modifiedPlacement.bannerDurationSeconds &&
      this.state.modifiedPlacement.bannerDurationSeconds > 0
        ? this.state.modifiedPlacement.bannerDurationSeconds
        : null

    if (
      this.state.modifiedPlacement.bannerDurationSeconds != null &&
      this.state.modifiedPlacement.bannerDurationSeconds <= 0
    ) {
      this.setState({
        form: {
          errors: {
            bannerDurationSeconds: 'must be a positive number',
          },
        },
        isLoading: false,
      })
      return
    }

    const videoContentCollectionId =
      this.state.modifiedPlacement.videoContentCollectionId || null
    const targetingRules = this.state.modifiedPlacement.targetingRules || []
    const { integrationType, integrationTypeConfig } =
      this.state.modifiedPlacement
    const integrationErrorCode = getIntegrationTypeErrorCode({
      integrationType,
      config: integrationTypeConfig,
    })
    if (integrationErrorCode) {
      this.setState({
        form: {
          errors: {
            integrationType: integrationErrorCode,
          },
        },
        isLoading: false,
      })
      return
    }
    const mutationPromise = !isCreation
      ? updateOutstreamPlacement(
          this.props.collectionId,
          this.props.modifiedPlacement!.id,
          title,
          integration,
          videoContentCollectionId,
          uniq(tags),
          whitelistGlobs,
          blacklistGlobs,
          maximumAdLength,
          bannerDurationSeconds,
          this.state.modifiedPlacement.integrationType,
          this.state.modifiedPlacement.integrationTypeConfig,
          targetingRules
        )
      : createOutstreamPlacement(
          this.props.collectionId,
          title,
          integration,
          videoContentCollectionId,
          uniq(tags),
          whitelistGlobs,
          blacklistGlobs,
          maximumAdLength,
          bannerDurationSeconds,
          this.state.modifiedPlacement.integrationType,
          this.state.modifiedPlacement.integrationTypeConfig,
          targetingRules
        )
    mutationPromise
      .then((id) =>
        isCreation
          ? updateOutstreamPlacement(
              this.props.collectionId,
              id,
              title,
              integration,
              videoContentCollectionId,
              uniq([...tags, id]),
              whitelistGlobs,
              blacklistGlobs,
              maximumAdLength,
              bannerDurationSeconds,
              this.state.modifiedPlacement.integrationType,
              this.state.modifiedPlacement.integrationTypeConfig,
              targetingRules
            )
          : id
      )
      .then(() => this.close())
      .then(this.props.onUpdated)
      .catch((e) => {
        console.error(e)
        this.setState({
          hasError: true,
          isLoading: false,
        })
      })
    return false
  }
  close() {
    this.props.onClose()
  }
}

const withGql = graphql<InputProps, OutstreamPlacementCollectionTagsResponse>(
  getOutstreamPlacementCollectionTagsQuery,
  {
    options: ({ collectionId }) => ({
      notifyOnNetworkStatusChange: true,
      variables: { collectionId },
    }),
  }
)
export const CreateOutstreamPlacementDialog = withGql(
  CreateOutstreamPlacementDialogView
)
